import { DropdownMenu as KobalteDropdownMenu } from "@kobalte/core/dropdown-menu"
import { type AccentHue, accentHues } from "@repo/fika-design-system/color"
import { createMemo, For, type ParentProps } from "solid-js"
import {
  dropdownContentClass,
  dropdownItemLabelClass,
  dropdownItemRecipe,
} from "@/designSystem/components/dropdown-menu.css"
import { Icon } from "@/designSystem/components/icon"
import { useI18nContext } from "@/i18n/i18n-solid"
import {
  calloutColorSwatchClass,
  calloutColorSwatchRecipe,
} from "./callout-menu.css"

type CalloutMenuProps = ParentProps<{
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  triggerClass: string
  triggerLabel: string
  onTriggerPointerDown: () => void
  onTriggerClick: (event: MouseEvent) => void
  onTriggerKeyDown: (event: KeyboardEvent) => void
  onTriggerDragStart: (event: DragEvent) => void
  onTriggerDragEnd: () => void
  onUpdateHue: (hue: AccentHue) => void
  onDelete: () => void
}>

export type CalloutNodeMenuAttributes = {
  hue: AccentHue
  emoji: string
}

export function CalloutMenu(props: CalloutMenuProps) {
  const { LL } = useI18nContext()

  const hueOptions = createMemo(() =>
    accentHues.map(hue => ({
      value: hue,
      label: hue.charAt(0).toUpperCase() + hue.slice(1),
    })),
  )

  return (
    <KobalteDropdownMenu open={props.isOpen} onOpenChange={props.onOpenChange}>
      <KobalteDropdownMenu.Trigger
        class={props.triggerClass}
        aria-label={props.triggerLabel}
        data-block-node-menu-trigger="true"
        draggable="true"
        onPointerDown={props.onTriggerPointerDown}
        onClick={props.onTriggerClick}
        onKeyDown={props.onTriggerKeyDown}
        onDragStart={props.onTriggerDragStart}
        onDragEnd={props.onTriggerDragEnd}
      >
        {props.children}
      </KobalteDropdownMenu.Trigger>
      <KobalteDropdownMenu.Portal>
        <KobalteDropdownMenu.Content class={dropdownContentClass}>
          <KobalteDropdownMenu.Sub>
            <KobalteDropdownMenu.SubTrigger
              class={dropdownItemRecipe({ hue: "sand" })}
            >
              <span class={calloutColorSwatchClass} />
              <span class={dropdownItemLabelClass}>
                {LL().editor.highlightColor()}
              </span>
              <Icon name="chevron-right" size="18" />
            </KobalteDropdownMenu.SubTrigger>
            <KobalteDropdownMenu.Portal>
              <KobalteDropdownMenu.SubContent class={dropdownContentClass}>
                <For each={hueOptions()}>
                  {option => (
                    <KobalteDropdownMenu.Item
                      class={dropdownItemRecipe({ hue: option.value })}
                      onSelect={() => props.onUpdateHue(option.value)}
                    >
                      <span
                        class={calloutColorSwatchRecipe({ hue: option.value })}
                      />
                      <span class={dropdownItemLabelClass}>{option.label}</span>
                    </KobalteDropdownMenu.Item>
                  )}
                </For>
              </KobalteDropdownMenu.SubContent>
            </KobalteDropdownMenu.Portal>
          </KobalteDropdownMenu.Sub>
          <KobalteDropdownMenu.Item
            class={dropdownItemRecipe({ hue: "rose" })}
            onSelect={props.onDelete}
          >
            <Icon name="trash-2" size="18" />
            <span class={dropdownItemLabelClass}>{LL().common.delete()}</span>
          </KobalteDropdownMenu.Item>
        </KobalteDropdownMenu.Content>
      </KobalteDropdownMenu.Portal>
    </KobalteDropdownMenu>
  )
}
